import React, { useMemo, useState } from "react";
import { Avatar } from "../shared/Avatar";
import { CustomIcon } from "../shared/CustomIcon";
import { fullName, formatDate } from "../../lib/format";
import { buildCSV, downloadCSV, Sparkline } from "./_utils";

// ============================================================
// Attendance tab — per-service progress bar with present/absent counts.
// Clicking a count opens a list of exact member names. Date-range filter.
// CSV export + a per-row attendance % trend sparkline.
// ============================================================
export default function AttendanceTab({ services, events, attendance, members }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [drillDown, setDrillDown] = useState(null); // { title, subtitle, names: [] }

  // Aggregate by service AND by event
  const stats = useMemo(() => {
    const inR = (dateStr) => {
      if (!dateStr) return true;
      const d = new Date(dateStr);
      if (from && d < new Date(from)) return false;
      if (to && d > new Date(`${to}T23:59:59`)) return false;
      return true;
    };
    const memberById = {};
    (members || []).forEach(m => { memberById[m.id] = m; });

    const rowsFromList = (list, kind, keyField) => list.map(entity => {
      const recs = (attendance || []).filter(a => a[keyField] === entity.id && inR(a.date));
      let present = 0, absent = 0;
      const presentIds = new Set();
      const absentIds = new Set();
      // Per-record attendance % for trend line (chronologically sorted).
      const trend = recs
        .slice()
        .sort((a, b) => (a.date || "").localeCompare(b.date || ""))
        .map(a => {
          let p = 0, ab = 0;
          Object.values(a.records || {}).forEach(s => { if (s === "present") p++; else ab++; });
          const t = p + ab;
          return t > 0 ? Math.round((p / t) * 100) : 0;
        });
      recs.forEach(a => {
        Object.entries(a.records || {}).forEach(([mid, status]) => {
          if (status === "present") { present += 1; presentIds.add(mid); }
          else { absent += 1; absentIds.add(mid); }
        });
      });
      const total = present + absent;
      const rate = total > 0 ? Math.round((present / total) * 100) : 0;
      return {
        id: entity.id,
        kind,
        name: entity.name,
        sessions: recs.length,
        present, absent, rate,
        trend,
        presentNames: Array.from(presentIds).map(id => memberById[id]).filter(Boolean).map(m => fullName(m)).sort(),
        absentNames:  Array.from(absentIds).map(id => memberById[id]).filter(Boolean).map(m => fullName(m)).sort(),
      };
    });

    return [
      ...rowsFromList(services || [], "Service", "serviceId"),
      ...rowsFromList(events || [],   "Event",   "eventId"),
    ];
  }, [services, events, attendance, members, from, to]);

  const totalCount = stats.length;
  const recorded = stats.filter(s => s.sessions > 0).length;

  const exportCsv = () => {
    const csv = buildCSV({
      headers: ["Kind", "Name", "Sessions", "Present", "Absent", "Attendance %"],
      rows: stats.map(s => [s.kind, s.name, s.sessions, s.present, s.absent, s.rate]),
    });
    downloadCSV(`attendance${from || to ? `-${from || "start"}_to_${to || "now"}` : ""}.csv`, csv);
  };

  return (
    <div data-testid="analytics-attendance-tab">
      <div className="att-toolbar">
        <div className="att-toolbar-counts">{recorded} of {totalCount} have attendance recorded</div>
        <div className="att-toolbar-filters">
          <label className="att-filter">
            <span>From</span>
            <input type="date" data-testid="att-from" value={from} onChange={e => setFrom(e.target.value)} />
          </label>
          <label className="att-filter">
            <span>To</span>
            <input type="date" data-testid="att-to" value={to} onChange={e => setTo(e.target.value)} />
          </label>
          {(from || to) && (
            <button type="button" className="btn btn-outline btn-sm" data-testid="att-clear-range" onClick={() => { setFrom(""); setTo(""); }}>Clear</button>
          )}
          <button type="button" className="btn btn-outline btn-sm" data-testid="att-export-csv" onClick={exportCsv} disabled={stats.length === 0}>⬇ CSV</button>
        </div>
      </div>

      {stats.length === 0 ? (
        <div className="analytics-empty">No services or events to report on.</div>
      ) : (
        <div className="att-grid" data-testid="att-grid">
          {stats.map(s => (
            <div key={`${s.kind}-${s.id}`} className="att-card" data-testid={`att-card-${s.id}`}>
              <div className="att-card-head">
                <div className="att-card-title">
                  <span className="att-card-kind">{s.kind}</span>
                  <span className="att-card-name">{s.name}</span>
                </div>
                <div className="att-card-rate" data-testid={`att-card-rate-${s.id}`}>{s.rate}%</div>
              </div>
              <div className="att-card-bar" data-testid={`att-card-bar-${s.id}`}>
                <div className="att-card-bar-fill" style={{ width: `${s.rate}%` }} />
              </div>
              {s.trend.length >= 2 && (
                <div style={{ marginTop: 6 }} title="Attendance % over time">
                  <Sparkline data={s.trend} testId={`att-card-trend-${s.id}`} width={140} height={26} />
                </div>
              )}
              <div className="att-card-meta">
                <button
                  type="button"
                  data-testid={`att-card-present-${s.id}`}
                  className="att-meta-link present"
                  disabled={s.present === 0}
                  onClick={() => setDrillDown({ title: `Present at ${s.name}`, subtitle: `${s.present} member${s.present === 1 ? "" : "s"}`, names: s.presentNames })}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                >
                  <CustomIcon name="check" size={11} /> {s.present} present
                </button>
                <button
                  type="button"
                  data-testid={`att-card-absent-${s.id}`}
                  className="att-meta-link absent"
                  disabled={s.absent === 0}
                  onClick={() => setDrillDown({ title: `Absent from ${s.name}`, subtitle: `${s.absent} member${s.absent === 1 ? "" : "s"}`, names: s.absentNames })}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
                >
                  <CustomIcon name="close" size={11} /> {s.absent} absent
                </button>
                <span className="att-meta-sessions">{s.sessions} session{s.sessions === 1 ? "" : "s"}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {drillDown && (
        <div className="modal-overlay" data-testid="att-drilldown-modal" onClick={() => setDrillDown(null)}>
          <div className="modal fm-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <div>
                <div className="modal-title">{drillDown.title}</div>
                <div className="fm-modal-count">{drillDown.subtitle}</div>
              </div>
              <button className="modal-close" data-testid="att-drilldown-close" onClick={() => setDrillDown(null)} aria-label="Close">×</button>
            </div>
            <div className="modal-body fm-modal-body">
              {drillDown.names.length === 0 ? (
                <div className="analytics-empty">No members in this group.</div>
              ) : (
                <ol className="att-name-list" data-testid="att-drilldown-names">
                  {drillDown.names.map((n, i) => (
                    <li key={`${n}-${i}`} className="att-name-row">{n}</li>
                  ))}
                </ol>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
